import React, { createRef } from "react";
import fieldPropTypes from "./field-prop-types";
import { fieldValidation as validation } from "../utils/field-validation";
import { MessagingContext } from "../context/messaging-layer";
import { devWarn } from "../utils/dev-logger";
import { decodeEmail } from "../utils/email-decoder";
import ValidationMessage from "../theme/default/validation-message";
import { FIELD_PARAMS, FIELD_NAMES } from "../utils/constants";

const injectLinks = action => (textParts, link) =>
  textParts.flatMap(part => {
    if (link.url.includes(FIELD_NAMES.agreementPPN)) {
      const query = link.url.slice(link.url.indexOf("?") + 1).split("&");
      const params = query.reduce((paramsString, param) => {
        const [key, value] = param.split("=");
        return Object.assign(paramsString, {
          [key]: decodeURIComponent(value)
        });
      }, {});

      link.url = params.file_url;
    }

    if (typeof part === "string" && part.includes(link.string)) {
      const beforeLinkPart = part.substring(0, part.indexOf(link.string));
      const afterLinkPart = part.substring(
        part.indexOf(link.string) + link.string.length
      );
      const anchorProps = action
        ? { href: "#", onClick: action }
        : {
            href: link.url,
            target: "_blank",
            rel: "noreferrer noopener",
            className: link.className
          };

      return [
        beforeLinkPart,
        // eslint-disable-next-line react/jsx-props-no-spreading
        <a key={link.url} {...anchorProps}>
          {link.string}
        </a>,
        afterLinkPart
      ];
    } else {
      return part;
    }
  });

class FormCheckboxContainer extends React.Component {
  static updateControlValidation(field, appData, fieldRef) {
    validation[field.type].update(field, appData, { fieldRef });
  }

  static updateSelectedOption(field, checked, name) {
    validation[field.type].updateOption(field, checked, name);
  }

  constructor(props) {
    super(props);
    const { field, appData } = props;
    this.handleCheckboxClick = appData.handleCheckboxClick;
    this.handleCheckboxReturnArrayClick =
      appData.handleCheckboxReturnArrayClick;
    this.handleAgreementCheckboxClick = appData.handleAgreementCheckboxClick;

    this.fieldRef = createRef();

    FormCheckboxContainer.updateControlValidation(
      field,
      appData,
      this.fieldRef
    );
  }

  componentDidMount() {
    const { field, appData } = this.props;
    const { selected: checkboxValue } = field?.options?.[0] || {};

    // TODO: The problem which this fixed is to send a status of checkbox to backend after submitting form
    // whenever checkbox was checked or unchecked without updating it.
    // I think this needs to be unified/refactored later and we always need to send checkbox data to backend on submitting.
    // But right now I'm not sure if this unification wouldn't brake some other logic so, this condition was added.
    // After refactoring please remove TDFieldOptionCheckboxPreset parameter from telapp codebase as well.
    if (
      field.params.includes("TDFieldOptionCheckboxPreset") &&
      field?.options?.length === 1
    ) {
      this.setCheckboxValue(field);
    } else if (appData.isUnifiedRegistration && field?.options?.length === 1) {
      if (
        (typeof checkboxValue === "string" &&
          ["YES", "Y"].includes(checkboxValue)) ||
        checkboxValue
      ) {
        FormCheckboxContainer.updateSelectedOption(field, true);

        this.setCheckboxValue(field);
      }
    }
  }

  componentDidUpdate() {
    const { field, appData } = this.props;
    FormCheckboxContainer.updateControlValidation(
      field,
      appData,
      this.fieldRef
    );
  }

  handleCheckboxClickWrapper = event => {
    const { field } = this.props;
    const { checked } = event.target;
    if (field.params.includes("TDFieldOptionLocked")) {
      event.preventDefault();
      return;
    }
    this.resetFieldValidation();
    const paramName = field?.options?.[0]?.name;

    FormCheckboxContainer.updateSelectedOption(field, checked);
    if (field.params.includes("TDFieldOptionReturnFullBool")) {
      this.handleCheckboxClick(paramName, checked ? "YES" : "NO");
    } else {
      this.handleCheckboxClick(paramName, checked ? "Y" : "N");
    }
  };

  handleMultipleCheckboxClickWrapper = (event, option) => {
    const { field } = this.props;
    const { checked } = event.target;
    this.resetFieldValidation();
    FormCheckboxContainer.updateSelectedOption(field, checked, option.name);
    if (field.params.includes("TDFieldOptionReturnFullBool")) {
      this.handleCheckboxClick(option.name, checked ? "YES" : "NO");
    } else {
      this.handleCheckboxClick(option.name, checked ? "Y" : "N");
    }
  };

  handleMultipleCheckboxReturnArrayClickWrapper = (event, option) => {
    const { field } = this.props;
    const { checked } = event.target;
    this.resetFieldValidation();
    FormCheckboxContainer.updateSelectedOption(field, checked, option.name);
    this.handleCheckboxReturnArrayClick(field);
  };

  handleAgreementCheckboxClickWrapper = (event, option) => {
    const { field } = this.props;
    const { checked } = event.target;
    const fieldParams = field?.params?.split("|") || [];
    const isReadRequired =
      fieldParams?.includes(FIELD_PARAMS.fieldOptionReadRequired) &&
      field.name !== FIELD_NAMES.agreementPPN;
    if (checked && field.action?.data?.file_url && isReadRequired) {
      event.preventDefault();
      this.showAgreementDialog(field);
    } else {
      this.resetFieldValidation();
      FormCheckboxContainer.updateSelectedOption(field, checked, option.name);
      this.handleAgreementCheckboxClick(field.name, checked, field.action);
    }
  };

  handleActionLinkClickWrapper = event => {
    event.preventDefault();
    this.showAgreementDialog(this.props.field);
  };

  getAgreementDialogContent = urlContent => {
    let div = document.createElement("div");
    div.innerHTML = urlContent;
    div = decodeEmail(div);
    return (
      div.getElementsByClassName("add-extra-padding")[0]?.innerHTML ||
      urlContent
    );
  };

  safeAgreementFetch = fieldUrl =>
    new Promise(resolve => {
      fetch(fieldUrl)
        .then(response => resolve(response.text()))
        .catch(error => {
          devWarn("Error Fetching Agreement:", error);
          resolve("");
        });
    });

  showAgreementDialog = field => {
    const {
      appData: { settings }
    } = this.props;
    this.safeAgreementFetch(field.action.data.file_url).then(response => {
      this.context.showDialog({
        fullscreen: true,
        actions: [
          {
            label: settings.translations.cancel,
            onClick: () => this.setAcceptAgreement(false),
            className: "secondary",
            name: "close-dialog",
            ariaLabel: `${settings.translations.cancel} ${field.action.data?.agreement_name}`
          },
          {
            label: settings.translations.accept,
            onClick: () => this.setAcceptAgreement(true),
            name: "accept-dialog",
            ariaLabel: `${settings.translations.accept} ${field.action.data?.agreement_name}`
          }
        ],
        content: {
          children: (
            <div
              className='agreement-dialog-web-sdk'
              // temporary solution since we still don't know what exactly
              // resource we will use in production as agreement content
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: this.getAgreementDialogContent(response)
              }}
            />
          )
        }
      });
    });
  };

  setCheckboxValue = field => {
    const { name: checkboxName, selected: checkboxValue } = field?.options?.[0];

    if (typeof checkboxValue === "string") {
      this.handleCheckboxClick(checkboxName, checkboxValue);
    } else if (field.params.includes("TDFieldOptionReturnFullBool")) {
      this.handleCheckboxClick(checkboxName, checkboxValue ? "YES" : "NO");
    } else {
      this.handleCheckboxClick(checkboxName, checkboxValue ? "Y" : "N");
    }
  };

  setAcceptAgreement = accept => {
    const { field } = this.props;
    this.resetFieldValidation();
    FormCheckboxContainer.updateSelectedOption(field, accept);
    this.handleAgreementCheckboxClick(field.name, accept, field.action);
  };

  getIsChecked = option => {
    const {
      field,
      appData: { values }
    } = this.props;
    const paramName = field?.options?.[0]?.name;
    const fieldValue = values[paramName];

    if (option) {
      if (field.params.includes("TDFieldOptionReturnArray")) {
        return (
          (values[field.name] && values[field.name].includes(option.value)) ??
          option.selected
        );
      }
      field.options.forEach(item => {
        if (values[item.name] === "Y") {
          item.selected = true;
        }
      });
      return ["Y", "YES"].includes(values[option.name]);
    }
    if (fieldValue) {
      if (field.params.includes("TDFieldOptionIsAgreement") && field.action) {
        return fieldValue.accepted;
      } else if (field.params.includes("TDFieldOptionReturnFullBool")) {
        return fieldValue.includes("YES");
      } else {
        return fieldValue === "Y";
      }
    }
    return field.options[0].selected;
  };

  drawAllOptions = (options, invalid, Checkbox) => {
    const { field } = this.props;
    return options.map(option => (
      <div key={option.text} className='field-checkbox-wrapper-web-sdk'>
        <Checkbox
          name={option.name}
          key={option.text}
          checked={this.getIsChecked(option)}
          onChange={event =>
            field.params.includes("TDFieldOptionReturnArray")
              ? this.handleMultipleCheckboxReturnArrayClickWrapper(
                  event,
                  option
                )
              : this.handleMultipleCheckboxClickWrapper(event, option)
          }
          label={option.text}
          invalid={invalid}
          ariaLabel={option?.accessibility?.aria_label || option?.text}
        />
      </div>
    ));
  };

  resetFieldValidation() {
    const { name } = this.props.field;
    const { resetFieldValidation } = this.props.appData;
    resetFieldValidation(name);
  }

  isInvalid() {
    const { name } = this.props.field;
    const { fieldValidation } = this.props.appData;
    return fieldValidation[name] && !fieldValidation[name].isValid;
  }

  render() {
    const {
      field,
      components: { Checkbox, Label },
      appData: { fieldValidation, settings, isUnifiedRegistration }
    } = this.props;
    const { required_field } = settings?.translations || {};

    let label = field.options[0]?.text;

    if (field.options[0]?.links) {
      const injectLinksCallback =
        field.action?.data?.file_url && field.name !== FIELD_NAMES.agreementPPN
          ? this.handleActionLinkClickWrapper
          : null;
      label = field.options[0].links.reduce(injectLinks(injectLinksCallback), [
        field.options[0].text
      ]);
    }
    const { errorMessage } = fieldValidation[field.name] || {};

    let ariaLabel = field.options[0]?.accessibility?.aria_label || label;

    if (Array.isArray(label)) {
      ariaLabel = label[0] + label[1].props.children + label[2];
    }

    return field.options.length === 1 ? (
      <>
        {field.title && (
          <Label
            text={field.title}
            name={field.name}
            params={field.params}
            size='lg'
          />
        )}
        <div className='field-wrapper-web-sdk' ref={this.fieldRef}>
          <Checkbox
            name={field.name}
            checked={this.getIsChecked()}
            onChange={event => {
              if (
                field.params.includes("TDFieldOptionIsAgreement") &&
                field.action
              ) {
                this.handleAgreementCheckboxClickWrapper(
                  event,
                  field.options[0]
                );
              } else {
                this.handleCheckboxClickWrapper(event, field.options[0]);
              }
            }}
            label={label}
            invalid={this.isInvalid()}
            ariaLabel={ariaLabel?.replace("*", required_field)}
          />
          <ValidationMessage
            fieldName={field.name}
            isInvalid={this.isInvalid()}
            isUnifiedRegistration={isUnifiedRegistration}
          >
            {errorMessage}
          </ValidationMessage>
        </div>
      </>
    ) : (
      <>
        <Label text={field.title} name={field.name} size='lg' />
        <div className='field-wrapper-web-sdk'>
          {this.drawAllOptions(field.options, this.isInvalid(), Checkbox)}
          <ValidationMessage
            fieldName={field.name}
            isInvalid={this.isInvalid()}
            isUnifiedRegistration={isUnifiedRegistration}
          >
            {errorMessage}
          </ValidationMessage>
        </div>
      </>
    );
  }
}

FormCheckboxContainer.propTypes = fieldPropTypes;
FormCheckboxContainer.contextType = MessagingContext;

export default FormCheckboxContainer;
